import { executeNextLoop } from '@ifixit/helpers';
import type { LocaleCode } from '@ifixit/i18n/types';
import { useCart } from '@ifixit/shopify-cart-sdk/hooks';
import { Sheet, SheetContent, SheetPresence } from '@ifixit/ui/components/sheet';
import { Suspense, lazy, type ElementType } from 'react';
import type { CartImageComponent } from '../types';
import { CartDrawerTrigger } from './CartDrawerTrigger';
import { useCartDrawer } from './hooks/useCartDrawer';

const LazyCartDrawerContent = lazy(() => import('./CartDrawerContent'));

interface CartDrawerProps {
   ImageComponent: CartImageComponent;
   linkComponent?: ElementType;
   supportLinkComponent: ElementType;
   localeCode: LocaleCode;
   viewCartUrl: string;
   storeCode: string;
   showMerchBanner?: boolean;
   recordMerchExperiment: () => boolean;
}

export function CartDrawer({
   ImageComponent,
   linkComponent,
   localeCode,
   supportLinkComponent,
   viewCartUrl,
   storeCode,
   recordMerchExperiment,
}: CartDrawerProps) {
   const { isOpen, onOpen, onClose } = useCartDrawer();
   const cart = useCart();
   const cartData = cart.data ?? null;

   return (
      <>
         <CartDrawerTrigger
            onClick={() => executeNextLoop(onOpen)}
            hasItemsInCart={cartData?.hasItemsInCart}
         />
         <Sheet
            open={isOpen}
            onOpenChange={open => {
               if (!open) {
                  onClose();
               }
            }}
         >
            {/* The cap flips at Chakra's sm, 576px, which is not Tailwind's
                sm, 640px, so min-[576px] carries the 64px between them.
                sm:max-w-[436px] stays because it is what beats the
                variant's sm:max-w-sm from 640px up: Tailwind orders
                min-width variants by width, so min-[576px] alone would
                lose to it there. pr re-adds the safe-area padding p-0
                swallows. z-above-cart-bar keeps the PDP's floating add to
                cart bar from covering the drawer's checkout button. */}
            <SheetContent
               side="right"
               overlayClassName="z-above-cart-bar-overlay"
               className="z-above-cart-bar w-full max-w-[calc(100vw-60px)] gap-0 bg-white p-0 pt-safe-top pr-safe-right text-base text-gray-800 min-[576px]:max-w-[436px] sm:max-w-[436px]"
               data-mobile-appid="cart-drawer"
               data-testid="cart-drawer"
            >
               <SheetPresence>
                  <Suspense>
                     <LazyCartDrawerContent
                        ImageComponent={ImageComponent}
                        linkComponent={linkComponent}
                        supportLinkComponent={supportLinkComponent}
                        localeCode={localeCode}
                        viewCartUrl={viewCartUrl}
                        storeCode={storeCode}
                        recordMerchExperiment={recordMerchExperiment}
                     />
                  </Suspense>
               </SheetPresence>
            </SheetContent>
         </Sheet>
      </>
   );
}
